// ToastSystem.jsx - Complete Toast Notification System
import React, { useState, useEffect, createContext, useContext } from "react";

// Toast Context
const ToastContext = createContext();

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = "info", duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showSuccess = (message, duration = 5000) =>
    addToast(message, "success", duration);
  const showError = (message, duration = 6000) =>
    addToast(message, "error", duration);
  const showWarning = (message, duration = 5000) =>
    addToast(message, "warning", duration);
  const showInfo = (message, duration = 4000) =>
    addToast(message, "info", duration);

  return (
    <ToastContext.Provider
      value={{
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeToast,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

// Toast Container Component
const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        maxWidth: "400px",
      }}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );
};

// Icon Components
const SuccessIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

// Individual Toast Item Component
const ToastItem = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (toast.duration > 0) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev <= 0) {
            clearInterval(interval);
            return 0;
          }
          return prev - 100 / (toast.duration / 50);
        });
      }, 50);

      return () => clearInterval(interval);
    }
  }, [toast.duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onRemove, 300);
  };

  const getToastConfig = (type) => {
    switch (type) {
      case "success":
        return {
          icon: <SuccessIcon />,
          colors: {
            primary: "#10b981",
            background: "#ecfdf5",
            border: "#10b981",
            text: "#065f46",
          },
        };
      case "error":
        return {
          icon: <ErrorIcon />,
          colors: {
            primary: "#ef4444",
            background: "#fef2f2",
            border: "#ef4444",
            text: "#7f1d1d",
          },
        };
      case "warning":
        return {
          icon: <WarningIcon />,
          colors: {
            primary: "#f59e0b",
            background: "#fffbeb",
            border: "#f59e0b",
            text: "#78350f",
          },
        };
      case "info":
      default:
        return {
          icon: <InfoIcon />,
          colors: {
            primary: "#3b82f6",
            background: "#eff6ff",
            border: "#3b82f6",
            text: "#1e3a8a",
          },
        };
    }
  };

  const config = getToastConfig(toast.type);

  const toastStyle = {
    display: "flex",
    alignItems: "flex-start",
    padding: "16px",
    backgroundColor: config.colors.background,
    borderLeft: `4px solid ${config.colors.border}`,
    borderRadius: "8px",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    minWidth: "320px",
    maxWidth: "400px",
    transform: isVisible ? "translateX(0)" : "translateX(100%)",
    opacity: isVisible ? 1 : 0,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
  };

  const iconContainerStyle = {
    color: config.colors.primary,
    marginRight: "12px",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    marginTop: "2px",
  };

  const messageStyle = {
    flex: 1,
    color: config.colors.text,
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "1.5",
    margin: 0,
    fontFamily:
      'Inter, Roboto, "SF Pro Display", "Segoe UI", system-ui, -apple-system, sans-serif',
  };

  const closeButtonStyle = {
    background: "none",
    border: "none",
    color: config.colors.primary,
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "12px",
    flexShrink: 0,
    transition: "all 0.2s ease",
  };

  const progressBarStyle = {
    position: "absolute",
    bottom: 0,
    left: 0,
    height: "3px",
    backgroundColor: config.colors.primary,
    width: `${progress}%`,
    transition: "width 0.05s linear",
    borderRadius: "0 0 8px 0",
  };

  return (
    <div style={toastStyle}>
      <div style={iconContainerStyle}>{config.icon}</div>

      <p style={messageStyle}>{toast.message}</p>

      <button
        style={closeButtonStyle}
        onClick={handleClose}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = "rgba(0,0,0,0.1)";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = "transparent";
        }}
      >
        <CloseIcon />
      </button>

      {toast.duration > 0 && <div style={progressBarStyle}></div>}
    </div>
  );
};
